import { Link } from "react-router-dom";
import { LegalPage, Sec, type TocEntry } from "../components/LegalPage";
import { COMPANY, PLATFORM_LINKS } from "../lib/company";
import { EmailLink } from "../components/EmailLink";
import { GOOGLE_SCOPES, META_SCOPES } from "../lib/scopes";

const TOC: TocEntry[] = [
  { id: "password", title: "We never see your password" },
  { id: "tokens", title: "Four barriers around your tokens" },
  { id: "permissions", title: "We ask for the narrowest permissions that work" },
  { id: "media", title: "Your media stays private" },
  { id: "isolation", title: "Every record is scoped to your workspace" },
  { id: "leave", title: "You can leave at any moment" },
  { id: "who", title: "Who we are" },
  { id: "report", title: "Reporting a vulnerability" },
];

export function Security() {
  return (
    <div className="wrap wrap-legal">
      <LegalPage
        eyebrow={COMPANY.product}
        title="Security and trust"
        toc={TOC}
        showDates={false}
        intro={<>
          You are about to give a small company access to accounts that may be your livelihood.
          That deserves more than a reassuring sentence. Here is precisely what we can and cannot
          do, and how you can check it yourself.
        </>}
      >
        <Sec id="password" n={1} title="We never see your password">
          <p>
            When you connect an account, we do not show you a Facebook or Google login form. We
            send your browser to Facebook or Google, on their own domain, over their own
            connection. You type your password there.
          </p>
          <p>
            <strong>You can verify this yourself in two seconds:</strong> when the password field
            appears, look at the address bar. It will read{" "}
            <code>facebook.com</code> or <code>accounts.google.com</code>, with the padlock. If it
            ever says {COMPANY.site.replace("https://", "")} while asking for a social password,
            close the tab and email us — that is not our product.
          </p>
          <p>
            What comes back to us is an access token: a revocable key, scoped to the specific
            permissions you approved, that can be cancelled from your platform settings at any
            moment without changing your password.
          </p>
        </Sec>

        <Sec id="tokens" n={2} title="Four barriers around your tokens">
          <p>A token is still worth protecting. Each of these holds on its own:</p>
          <ol>
            <li><strong>Encryption.</strong> Tokens are encrypted with AES-256-GCM before they are
              written down.</li>
            <li><strong>Key separation.</strong> The encryption key lives in the server-side
              function environment, not in the database. A stolen database copy yields ciphertext
              and nothing else.</li>
            <li><strong>Not on the public API.</strong> Credentials live in a private database
              schema that our public API does not expose. There is no request any user can make —
              including you, including us from the browser — that returns a token.</li>
            <li><strong>No access policy at all.</strong> Row-level security is switched on for that
              storage with zero policies granting access, so ordinary application credentials are
              refused by default rather than by omission.</li>
          </ol>
          <p>
            Tokens are decrypted only inside the function that is publishing your post, for the
            duration of that one call. They are never sent to your browser or your phone.
          </p>
        </Sec>

        <Sec id="permissions" n={3} title="We ask for the narrowest permissions that work">
          <p>
            Every permission below is required by a specific step in publishing. There are no
            spare ones held for a future feature.
          </p>
          <h3>Facebook and Instagram</h3>
          <div className="tbl-wrap">
            <table className="tbl">
              <thead><tr><th>Permission</th><th>Why we need it</th></tr></thead>
              <tbody>
                {META_SCOPES.map(([scope, why]) => (
                  <tr key={scope}><td><code>{scope}</code></td><td>{why}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
          <h3>YouTube</h3>
          <div className="tbl-wrap">
            <table className="tbl">
              <thead><tr><th>Scope</th><th>Why we need it</th></tr></thead>
              <tbody>
                {GOOGLE_SCOPES.map(([scope, why]) => (
                  <tr key={scope}><td><code>{scope}</code></td><td>{why}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
          <p>
            Notice what is absent: no permission to read your inbox, your messages, your followers,
            your friends list, your ad accounts or your existing content library. We cannot read
            them because we never asked to.
          </p>
        </Sec>

        <Sec id="media" n={4} title="Your media stays private">
          <p>
            Uploads go to a private bucket. Nothing is public, indexed or listable, and access is
            scoped to your workspace.
          </p>
          <p>
            Facebook and Instagram publish by fetching a URL rather than accepting an upload, so at
            publish time we mint a signed link that is unguessable and expires in two hours, hand
            it to Meta, and let it lapse. YouTube needs the bytes, so videos go from your device
            straight to Google&rsquo;s upload endpoint without passing through us.
          </p>
        </Sec>

        <Sec id="isolation" n={5} title="Every record is scoped to your workspace">
          <p>
            Isolation between customers is enforced in the database, not in application code. Each
            table carries a row-level security policy tying every row to a workspace you belong to.
            A bug in a screen cannot leak another customer&rsquo;s posts, because the query itself
            would return nothing.
          </p>
        </Sec>

        <Sec id="leave" n={6} title="You can leave at any moment">
          <p>
            Disconnect an account in {COMPANY.product} and its stored token is deleted immediately.
            Or cut us off at the source, without visiting our site at all:
          </p>
          <ul>
            <li>
              <a href={PLATFORM_LINKS.metaAppSettings} target="_blank" rel="noreferrer noopener">
                Facebook &rarr; Settings &rarr; Business Integrations
              </a>
            </li>
            <li>
              <a href={PLATFORM_LINKS.googlePermissions} target="_blank" rel="noreferrer noopener">
                Google &rarr; Security &rarr; Third-party apps with account access
              </a>
            </li>
          </ul>
          <p>
            Full deletion of everything we hold is described step by step on{" "}
            <Link to="/data-deletion">Delete your data</Link>. How long each kind of data is kept
            before that, including Google user data, is set out in the{" "}
            <Link to="/privacy">Privacy Policy</Link>.
          </p>
        </Sec>

        <Sec id="who" n={7} title="Who we are">
          <p>
            {COMPANY.product} is a product of {COMPANY.legalName}, a registered Indian company
            at {COMPANY.address.oneLine}. It is made with{" "}
            <a href={COMPANY.studio.url} target="_blank" rel="noreferrer noopener">
              {COMPANY.studio.name} &mdash; {COMPANY.studio.tagline}
            </a>, the company&rsquo;s own product tooling; it is not a third party and no
            credential or media file passes through it. We are a named, addressable,
            incorporated entity — not an anonymous app listing. More on{" "}
            <Link to="/about">About us</Link>.
          </p>
        </Sec>

        <Sec id="report" n={8} title="Reporting a vulnerability">
          <p>
            If you find a security problem, email{" "}
            <EmailLink address={COMPANY.supportEmail} /> with the subject{" "}
            <strong>Security</strong>. Tell us what you found and how to reproduce it. We will
            acknowledge within two business days and keep you updated until it is closed. We will
            not pursue legal action against anyone who reports a genuine issue in good faith and
            gives us a reasonable chance to fix it before disclosing.
          </p>
        </Sec>
      </LegalPage>
    </div>
  );
}
